import React from 'react';

export interface GlassInputProps {
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'date';
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  icon?: React.ReactNode;
  error?: string;
  label?: string;
  disabled?: boolean;
  className?: string;
  name?: string;
  id?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  required?: boolean;
}

export const GlassInput: React.FC<GlassInputProps> = ({
  type = 'text',
  placeholder,
  value,
  onChange,
  icon,
  error,
  label,
  disabled = false,
  className = '',
  name,
  id,
  autoComplete,
  autoFocus = false,
  onFocus,
  onBlur,
  required = false,
}) => {
  const inputId = id || name || `glass-input-${Math.random().toString(36).substr(2, 9)}`;

  const containerStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-1)',
    width: '100%',
  };

  const labelStyles: React.CSSProperties = {
    fontSize: 'var(--text-sm)',
    fontWeight: 'var(--font-medium)',
    color: 'var(--text-secondary)',
  };

  const inputWrapperStyles: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  };

  const getInputStyles = (): React.CSSProperties => ({
    width: '100%',
    padding: icon ? 'var(--space-3) var(--space-4) var(--space-3) var(--space-10)' : 'var(--space-3) var(--space-4)',
    fontSize: 'var(--text-base)',
    fontFamily: 'var(--font-family)',
    color: 'var(--text)',
    background: 'var(--panel)',
    border: `1px solid ${error ? 'var(--danger)' : 'var(--stroke)'}`,
    borderRadius: 'var(--radius-md)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    transition: 'all var(--transition-normal)',
    outline: 'none',
    cursor: disabled ? 'not-allowed' : 'text',
    opacity: disabled ? 0.6 : 1,
  });

  const iconStyles: React.CSSProperties = {
    position: 'absolute',
    left: 'var(--space-3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--text-muted)',
    pointerEvents: 'none',
  };

  const errorStyles: React.CSSProperties = {
    fontSize: 'var(--text-xs)',
    color: 'var(--danger)',
    marginTop: 'var(--space-1)',
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = 'var(--primary-500)';
    e.currentTarget.style.boxShadow = '0 0 0 3px var(--primary-100)';
    onFocus?.();
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = error ? 'var(--danger)' : 'var(--stroke)';
    e.currentTarget.style.boxShadow = 'none';
    onBlur?.();
  };

  return (
    <div className={`glass-input-container ${className}`} style={containerStyles}>
      {label && (
        <label htmlFor={inputId} style={labelStyles}>
          {label}
        </label>
      )}
      <div style={inputWrapperStyles}>
        {icon && <span style={iconStyles}>{icon}</span>}
        <input
          id={inputId}
          name={name}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          required={required}
          style={getInputStyles()}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </div>
      {error && <span style={errorStyles}>{error}</span>}
    </div>
  );
};

export default GlassInput;
